import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useFriendsStore } from '../stores/friendsStore';
import { useDialogStore } from '../stores/dialogStore';
import ProfileAvatar from './ProfileAvatar';

const Friends = ({ profileIdOverride = null }) => {
  const { profileId: urlProfileId } = useParams();
  const { accessToken, profile } = useAuthStore();

  // Use override if provided (for Dashboard), otherwise use URL param (for profile pages)
  const profileId = profileIdOverride || urlProfileId;

  const {
    friends,
    pendingRequests,
    isLoading,
    showAll,
    isOwnProfile,
    outgoingRequest,
    areFriends,
    totalFriends,
    fetchFriends,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    unfriend,
    toggleShowAll
  } = useFriendsStore();
  const { confirm, alert } = useDialogStore();

  // Fetch friends when component mounts or profileId changes
  useEffect(() => {
    if (profileId && accessToken) {
      fetchFriends(profileId, accessToken, false);
    }
  }, [profileId, accessToken, fetchFriends]);

  const handleSendRequest = async () => {
    const result = await sendFriendRequest(profileId, accessToken);
    if (!result.success) {
      alert('Error', result.error || 'Failed to send friend request');
    }
  };

  const handleAccept = async (friendshipId) => {
    const result = await acceptFriendRequest(friendshipId, accessToken, profileId);
    if (!result.success) {
      alert('Error', result.error || 'Failed to accept friend request');
    }
  };

  const handleReject = async (friendshipId) => {
    const result = await rejectFriendRequest(friendshipId, accessToken, profileId);
    if (!result.success) {
      alert('Error', result.error || 'Failed to reject friend request');
    }
  };

  const handleUnfriend = async (friendProfileId, friendName) => {
    const confirmed = await confirm(
      'Unfriend',
      `Are you sure you want to remove ${friendName} from your friends?`
    );
    if (!confirmed) return;

    const result = await unfriend(friendProfileId, accessToken, profileId);
    if (!result.success) {
      alert('Error', result.error || 'Failed to unfriend');
    }
  };

  const handleToggleShowAll = () => {
    toggleShowAll(profileId, accessToken);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading && friends.length === 0) {
    return (
      <div className="surface rounded-lg shadow-lg p-4">
        <h3 className="font-semibold text-primary mb-4">Friends</h3>
        <div className="flex justify-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-4 friends-list-container">
      <h3 className="font-semibold text-primary mb-4">
        Friends {totalFriends > 0 && `(${totalFriends})`}
      </h3>

      {/* Add Friend Button (if viewing someone else's profile who is not a friend) */}
      {!isOwnProfile && !areFriends && !outgoingRequest && (
        <button
          onClick={handleSendRequest}
          className="w-full mb-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
        >
          + Add Friend
        </button>
      )}

      {/* Request Sent Indicator */}
      {!isOwnProfile && outgoingRequest && (
        <div className="w-full mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-secondary rounded text-center">
          Request Sent
        </div>
      )}

      {/* Pending Incoming Requests (only for own profile) */}
      {isOwnProfile && pendingRequests.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-secondary mb-2">Pending Requests</h4>
          {pendingRequests.map((request) => (
            <div key={request.id} className="mb-3 p-3 bg-gray-100 dark:bg-gray-800 rounded">
              <div className="flex items-center gap-2 mb-2">
                <Link to={`/profile/${request.requester_profile_id}/page/dashboard`}>
                  <ProfileAvatar
                    profileId={request.requester_profile_id}
                    displayName={request.display_name}
                    size="sm"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/profile/${request.requester_profile_id}/dashboard`}
                    className="text-sm font-medium text-primary hover:text-primary-700 truncate block"
                  >
                    {request.display_name}
                  </Link>
                  <p className="text-xs text-secondary">{formatTimeAgo(request.requested_at)}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAccept(request.id)}
                  className="flex-1 px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleReject(request.id)}
                  className="flex-1 px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Friends List */}
      {friends.length > 0 ? (
        <div className="space-y-2">
          {friends.map((friend) => {
            // Determine friend profile ID (the one that's not the current profile)
            const friendProfileId = friend.requester_profile_id == profileId
              ? friend.addressee_profile_id
              : friend.requester_profile_id;

            return (
              <div key={friend.id} className="flex items-center gap-2 group">
                <Link to={`/profile/${friendProfileId}/page/dashboard`} className="flex items-center gap-2 flex-1 min-w-0">
                  <ProfileAvatar
                    profileId={friendProfileId}
                    displayName={friend.display_name}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary hover:text-primary-700 truncate">
                      {friend.display_name}
                    </p>
                    <p className="text-xs text-secondary">{friend.role_label}</p>
                  </div>
                </Link>
                {isOwnProfile && (
                  <button
                    onClick={() => handleUnfriend(friendProfileId, friend.display_name)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-opacity"
                    title="Unfriend"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}

          {/* Show All / Show Less Button */}
          {totalFriends > 5 && (
            <button
              onClick={handleToggleShowAll}
              className="w-full mt-3 px-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              {showAll ? 'Show Less' : `Show All (${totalFriends})`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-secondary text-center py-4">
          {isOwnProfile ? 'No friends yet' : 'No friends to display'}
        </p>
      )}
    </div>
  );
};

export default Friends;
